import React, { useEffect, useState } from "react";
import { getTheme, mergeStyleSets } from "@fluentui/react";

import { ItemRenderProps, TreeView, TreeViewExpandChangeEvent } from "@progress/kendo-react-treeview";
import "@progress/kendo-theme-bootstrap/dist/all.css";
import { SkeletonItem } from "@fluentui/react-components";
import { CoSuspense } from "../../../common/loading/CoSuspense";
import { SkeletonLoader } from "../../../common/loading/SkeletonLoader";
import { mapNodeToDataItem } from "../../../api/object/Dimensions";
import { DimensionDetail } from "../../../api/models/dimension-detail";

interface TreeState {
  treeItems: DimensionTreeItem[];
}

export interface DimensionItem {
  oid: string;
  code: string;
  label?: string;
  type?: string;
  desc: string;
  nodeType: string;
  index: number;
  leaf: boolean;
  hierarchical: boolean;
  mandatory?: boolean;
  multiSelectable?: boolean;
  selectable?: boolean;
  parent: DimensionItem;
  children: DimensionItem[];
  data: any;
}

export type DimensionTreeItem = Omit<DimensionItem, "children"> & {
  expanded?: boolean;
  checked?: boolean;
  hasChildren: boolean;
  children: DimensionTreeItem[];
  isSkeleton?: boolean;
};

export type FormTreeSelection = DimensionTreeItem & {
  oidPath: string[];
};

interface FormTreeProps {
  rootKey: string;
  dimension: DimensionDetail;
  onChange: (data: FormTreeSelection) => void;
  className?: string;
  fetchRoot: (parentOid: string) => Promise<DimensionItem[]>;
  fetchChildren: (parent: DimensionItem) => Promise<DimensionItem[]>;
  currentHierarchy: DimensionItem | undefined;
  hidden?: boolean;
}

const uncheckAll = (items: DimensionTreeItem[]) => {
  return items.map((i) => ({
    ...i,
    checked: false,
    children: uncheckAll(i.children ?? []),
  }));
};

async function retrieveCheckedData(
  oidPath: string[],
  roots: DimensionTreeItem[],
  fetchChildren: (item: DimensionItem) => Promise<DimensionItem[]>
): Promise<DimensionTreeItem[]> {
  const nodeOidHierarchy = oidPath.slice(0, oidPath.length - 1);
  const nodeOid = oidPath[oidPath.length - 1];

  let items = roots;
  //navigate until items are brother of the items we need to check
  for (const checkOid of nodeOidHierarchy) {
    const fatherIndex = items.findIndex((value) => value.oid === checkOid);
    const father = items[fatherIndex];
    if (!father.children.length) {
      father.children = (await fetchChildren(father)).map((fetched) => {
        return mapNodeToDataItem(fetched);
      });
    }
    father.expanded = true;

    items = father.children;
  }
  // find item and check
  const itemToCheckIndex = items.findIndex((item) => item.oid === nodeOid);
  if (itemToCheckIndex !== -1) items[itemToCheckIndex].checked = true;
  return roots;
}

const retrieveAll = async (
  dimension: DimensionDetail | undefined,
  _roots: DimensionTreeItem[],
  fetchChildren: (item: DimensionItem) => Promise<DimensionItem[]>
) => {
  return await (dimension?.values ?? []).reduce<Promise<DimensionTreeItem[]>>(async (rootsPromise, value) => {
    const roots = await rootsPromise;
    return retrieveCheckedData(value.oidPath, roots, fetchChildren);
  }, Promise.resolve(_roots));
};

export const FormTree: React.FunctionComponent<FormTreeProps> = ({
  rootKey,
  dimension,
  onChange,
  className,
  fetchRoot,
  fetchChildren,
  currentHierarchy,
  hidden,
}) => {
  // state hooks
  const [state, setState] = useState<TreeState>({ treeItems: [] });
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  //TODO refactor merging the following two useEffect
  // into one conditionally calling fetchRoot
  // if (!state.treeItems?.length && dbId && rootKey)
  useEffect(() => {
    let itemsPromise: Promise<DimensionItem[]> | null = null;
    if (dimension.hierarchy) {
      itemsPromise = fetchChildren({
        data: currentHierarchy.data,
        oid: currentHierarchy.data.id,
      } as any);
    } else if (rootKey) {
      itemsPromise = fetchRoot(rootKey);
    }

    if (itemsPromise != null) {
      //checks if itemsPromise is a Promise
      setIsInitialLoading(true);
      itemsPromise
        ?.then(async (items) => {
          let treeItems = items.map((item) => mapNodeToDataItem(item));
          if (dimension.values) {
            treeItems = await retrieveAll(dimension, treeItems, fetchChildren);
          }
          setState({
            treeItems,
          });
        })
        .finally(() => setIsInitialLoading(false));
    }
  }, [rootKey, currentHierarchy]);

  useEffect(() => {
    if (!hidden && state.treeItems.length) {
      retrieveAll(dimension, uncheckAll(state.treeItems), fetchChildren).then((value) => {
        setState({
          treeItems: value,
        });
      });
    }
  }, [dimension.values, dimension.hierarchy, hidden]);

  const onExpandChange = (event: TreeViewExpandChangeEvent) => {
    if (event.item.hasChildren) {
      const indexes = event.itemHierarchicalIndex.split("_").map((value) => Number(value));
      const { items, item } = copyItemsAndItem(state.treeItems, indexes);
      item.expanded = !item.expanded;
      if (!item.children.length) {
        const skeletonChildren: DimensionTreeItem[] = [1].map((i) => ({
          oid: `__skeleton__${i}`,
          code: "",
          desc: "",
          nodeType: "",
          index: i,
          leaf: true,
          hierarchical: false,
          selectable: false,
          multiSelectable: false,
          mandatory: false,
          parent: null,
          data: null,
          hasChildren: false,
          children: [],
          isSkeleton: true,
        }));
        item.children = skeletonChildren;
        setState({ ...state, treeItems: items });
        fetchChildren(item).then((dataForms) => {
          const { items: children, item: parent } = copyItemsAndItem(items, indexes);
          if (dataForms.length) {
            parent.children = dataForms.map((form) => mapNodeToDataItem(form));
          } else {
            parent.expanded = false;
            parent.hasChildren = false;
            parent.children = [];
          }
          setState({ ...state, treeItems: children });
        });
      } else {
        setState({ ...state, treeItems: items });
      }
    }
  };

  const onCheckChange = (event: { item: DimensionTreeItem; itemHierarchicalIndex: string }) => {
    if (event.item.isSkeleton) return;
    const indexes = event.itemHierarchicalIndex.split("_").map((value) => Number(value));
    const { items, item } = copyItemsAndItem(state.treeItems, indexes);
    const thisItemPath = treePath(state.treeItems, indexes).map((itemInHierarchy) => itemInHierarchy.oid);
    item.checked = !item.checked;
    setState({ ...state, treeItems: items });

    if (onChange) {
      onChange({
        ...item,
        oidPath: thisItemPath,
      });
    }
  };

  return (
    <CoSuspense isLoading={isInitialLoading} loadingComponent={<SkeletonLoader />}>
      {state.treeItems.length ? (
        <TreeView
          data-testid="tree-view"
          data={state.treeItems}
          textField="desc"
          childrenField="children"
          expandIcons={true}
          onExpandChange={onExpandChange}
          className={className}
          item={TreeViewItem({ onCheckChange })}
        />
      ) : null}
    </CoSuspense>
  );
};

export const TreeViewItem =
  ({ onCheckChange }: { onCheckChange: (event: { item: DimensionTreeItem; itemHierarchicalIndex: string }) => void }) =>
  // eslint-disable-next-line react/display-name
  (props: ItemRenderProps) => {
    if (props.item.isSkeleton) {
      return <SkeletonItem id="skeleton" style={{ height: 20, marginBottom: 4, width: "80%" }} animation="pulse" />;
    }
    return (
      <div style={{ display: "flex", alignItems: "center", columnGap: "1rem" }}>
        <input
          id={props.item?.desc ?? props.item.desc}
          type={"checkbox"}
          className={"k-checkbox k-rounded-md k-checkbox-md"}
          style={{ backgroundColor: props.item.selectable ? "none" : "var(--color-disabled-bg)" }}
          disabled={!props.item.selectable}
          checked={props.item.checked}
          onClick={() =>
            onCheckChange({
              item: props.item,
              itemHierarchicalIndex: props.itemHierarchicalIndex,
            })
          }
          readOnly
          key={"0"}
        />{" "}
        {props.item.code?.length ? props.item.code + " - " + props.item.desc : props.item.desc}
      </div>
    );
  };

export const copyItemsAndItem = (
  items: DimensionTreeItem[],
  indexes: number[]
): { items: DimensionTreeItem[]; item: DimensionTreeItem } => {
  const copiedItems = items.slice();
  const item = (copiedItems[indexes[0]] = { ...copiedItems[indexes[0]] });

  if (indexes.length > 1) {
    const { items: children, item: target } = copyItemsAndItem(item.children, indexes.slice(1));
    item.children = children;
    return { items: copiedItems, item: target };
  } else {
    return { items: copiedItems, item: item };
  }
};

export const treePath = (items: DimensionTreeItem[], indexes: number[]): DimensionTreeItem[] => {
  const item = items[indexes[0]];
  if (item?.children?.length && indexes?.length > 1) {
    return [item, ...treePath(item.children, indexes.slice(1))];
  } else {
    return [item];
  }
};

export const ScrollableFormTree: React.FunctionComponent<FormTreeProps & { height?: string }> = (props) => {
  const theme = getTheme();
  const classNames = mergeStyleSets({
    wrapper: {
      height: props.height,
      position: "relative",
      maxHeight: "inherit",
      border: "1px solid " + theme.palette.neutralLight,
      overflow: "auto",
      display: props.hidden ? "none" : undefined,
    },
    tree: {
      display: "inline-block !important",
    },
  });

  return (
    <div className={classNames.wrapper}>
      <FormTree {...props} className={classNames.tree} />
    </div>
  );
};
